// Lab 3: multi-instance Paxos. Each server is proposer, acceptor, and
// learner. One Paxos instance chooses the command for each log slot; replicas
// execute only a contiguous prefix of chosen slots. The leader proposes one
// slot at a time, which keeps the core small without weakening safety.
//
// Simplifications, stated plainly: sequential (not concurrent) slot proposal,
// and fixed membership. Everything else follows the lab spec: ballot-ordered
// prepare/promise with log merge, majority acceptance, heartbeat learning,
// lagging recovery, and all-replica log compaction.
//
// config: { servers, clients, linkDelay=1, pingInterval=4, checkInterval=5,
//           electionTimeout=9, acceptTimeout=5, clientRetry=6 }
import { EventKind } from '../../core/types';
import { amoApply } from '../common/amo';
import { createKvStore, kvApply } from '../common/kvStore';
import { checkLab3Invariants } from './invariants';

export const MSG_PREPARE = 'prepare';
export const MSG_PROMISE = 'promise';
export const MSG_ACCEPT = 'accept';
export const MSG_ACCEPTED = 'accepted';
export const MSG_PING = 'ping';
export const MSG_PING_REPLY = 'pingreply';
export const MSG_CREQUEST = 'crequest';
export const MSG_CREPLY = 'creply';

export const TIMER_PING = 'ping';
export const TIMER_CHECK = 'check';
export const TIMER_ACCEPT = 'accept';
export const TIMER_RETRY = 'retry';

export const labMeta = {
  id: 'lab3',
  title: 'Lab 3 · Multi-Paxos',
  description: 'Ballots, quorums, and a replicated log that every healthy replica executes in the same order.',
};

export function defaultConfig() {
  return {
    servers: ['S1', 'S2', 'S3'],
    clients: ['C1'],
    linkDelay: 1,
    pingInterval: 4,
    checkInterval: 5,
    electionTimeout: 9,
    acceptTimeout: 5,
    clientRetry: 6,
  };
}

export function majority(n) {
  return Math.floor(n / 2) + 1;
}

// Ballots order by number first, then address.
export function compareBallot(a, b) {
  if (a.n !== b.n) return a.n - b.n;
  if (a.address === b.address) return 0;
  return a.address < b.address ? -1 : 1;
}

function sameBallot(a, b) {
  return a.n === b.n && a.address === b.address;
}

function freshConsensus(self) {
  return {
    ballot: { n: 0, address: self },
    promised: { n: 0, address: '' },
    leader: null,
    isLeader: false,
    phase1Replies: [],
    phase2: {},
    log: [],
    nextSlot: 0,
    proposing: null,
    pendingCommands: [],
    executedAll: {},
    gcWatermark: -1,
    lastLeaderContact: 0,
  };
}

export function initialize(config) {
  const nodes = {};
  config.servers.forEach((id) => {
    nodes[id] = {
      durable: { role: 'paxos', self: id, kv: createKvStore(), amo: {}, appliedIds: [], served: [], executedThrough: -1, executedCount: 0 },
      volatile: freshConsensus(id),
    };
  });
  config.clients.forEach((id) => {
    nodes[id] = {
      durable: { role: 'paxosclient', nextSeq: 1, accepted: 0, sentIds: [], completed: [] },
      volatile: { outstanding: null, target: config.servers[0], retries: 0 },
    };
  });
  const initialEvents = [];
  config.servers.forEach((id, i) => {
    initialEvents.push({
      kind: EventKind.TIMER, from: id, to: id,
      payload: { timerKey: TIMER_PING, timerType: TIMER_PING },
      createdAt: 0, deliverAt: 1 + i, parentId: null, label: 'ping timer',
    });
    initialEvents.push({
      kind: EventKind.TIMER, from: id, to: id,
      payload: { timerKey: TIMER_CHECK, timerType: TIMER_CHECK },
      createdAt: 0, deliverAt: 2 + i, parentId: null, label: 'check timer',
    });
  });
  return { nodes, initialEvents };
}

export function freshVolatile(durable) {
  if (durable.role === 'paxosclient') return { outstanding: null, target: null, retries: 0 };
  return freshConsensus(durable.self);
}

function clone(part) {
  return JSON.parse(JSON.stringify(part));
}

function send(to, payload, delay, label) {
  return { to, payload, delay, label };
}

function broadcast(config, nodeId, payload, delay, label) {
  return config.servers
    .filter((s) => s !== nodeId)
    .map((s) => send(s, JSON.parse(JSON.stringify(payload)), delay, label));
}

function slotOf(log, i) {
  return log[i] || { status: 'empty' };
}

function acceptedEntries(volatile) {
  const out = [];
  volatile.log.forEach((slot, i) => {
    if (slot && (slot.status === 'accepted' || slot.status === 'chosen')) {
      out.push({
        slot: i, ballot: { ...slot.ballot }, command: slot.command,
        client: slot.client, seq: slot.seq, status: slot.status,
      });
    }
  });
  return out;
}

function chosenEntries(volatile) {
  return acceptedEntries(volatile).filter((e) => e.status === 'chosen');
}

// Execute the contiguous chosen prefix. Replies only via the outbox list the
// caller drains for commands this server owns as pending.
function executeContiguous(durable, volatile) {
  const completed = [];
  for (;;) {
    const next = slotOf(volatile.log, durable.executedThrough + 1);
    if (next.status !== 'chosen') break;
    durable.executedThrough += 1;
    durable.executedCount += 1;
    const outcome = amoApply(durable.amo, next.client, next.seq, () => {
      durable.appliedIds.push(next.client + '#' + next.seq);
      return kvApply(durable.kv, next.command);
    });
    const idx = volatile.pendingCommands.findIndex(
      (p) => p.client === next.client && p.seq === next.seq
    );
    if (idx >= 0) {
      const [pending] = volatile.pendingCommands.splice(idx, 1);
      completed.push({ pending, result: outcome.result });
    }
  }
  return completed;
}

function replyOutbox(config, nodeId, completed) {
  return completed.map(({ pending, result }) =>
    send(
      pending.client,
      { msgType: MSG_CREPLY, client: pending.client, seq: pending.seq, result, leaderHint: nodeId },
      config.linkDelay,
      'client reply'
    )
  );
}

function pingPayload(nodeId, durable, volatile) {
  return {
    msgType: MSG_PING,
    leader: nodeId,
    ballot: { ...volatile.ballot },
    chosen: chosenEntries(volatile),
    executedThrough: durable.executedThrough,
    gcWatermark: volatile.gcWatermark,
  };
}

// ---------------- leader proposal ----------------

function firstEmptySlot(volatile) {
  let s = volatile.nextSlot;
  while (slotOf(volatile.log, s).status !== 'empty') s += 1;
  volatile.nextSlot = s;
  return s;
}

function proposeNext(config, nodeId, durable, volatile, timerOps, outbox) {
  if (!volatile.isLeader || volatile.proposing !== null) return;
  // Adopted-but-unchosen slots from the election merge go first: the new
  // leader re-proposes their values under its own ballot.
  let adopted = -1;
  volatile.log.forEach((s, i) => {
    if (s && s.status === 'accepted' && (adopted < 0 || i < adopted)) adopted = i;
  });
  if (adopted >= 0) {
    const entry = volatile.log[adopted];
    volatile.proposing = adopted;
    volatile.phase2[adopted] = { ballot: { ...volatile.ballot }, accepts: [nodeId] };
    const payload = {
      msgType: MSG_ACCEPT, slot: adopted, ballot: { ...volatile.ballot },
      command: entry.command, client: entry.client, seq: entry.seq,
    };
    broadcast(config, nodeId, payload, config.linkDelay, 'accept slot ' + adopted).forEach((m) =>
      outbox.push(m)
    );
    timerOps.push({
      action: 'set', timerKey: TIMER_ACCEPT, timerType: TIMER_ACCEPT,
      payload: { slot: adopted }, delay: config.acceptTimeout, label: 'accept timer',
    });
    return;
  }
  const next = volatile.pendingCommands[0];
  if (!next) return;
  const slot = firstEmptySlot(volatile);
  volatile.proposing = slot;
  volatile.log[slot] = {
    status: 'accepted', ballot: { ...volatile.ballot },
    command: next.command, client: next.client, seq: next.seq,
  };
  volatile.phase2[slot] = { ballot: { ...volatile.ballot }, accepts: [nodeId] };
  const payload = {
    msgType: MSG_ACCEPT, slot, ballot: { ...volatile.ballot },
    command: next.command, client: next.client, seq: next.seq,
  };
  broadcast(config, nodeId, payload, config.linkDelay, 'accept slot ' + slot).forEach((m) =>
    outbox.push(m)
  );
  timerOps.push({
    action: 'set', timerKey: TIMER_ACCEPT, timerType: TIMER_ACCEPT,
    payload: { slot }, delay: config.acceptTimeout, label: 'accept timer',
  });
}

function checkChosenMajority(config, nodeId, durable, volatile, slot, outbox, timerOps, notes) {
  const round = volatile.phase2[slot];
  if (!round || volatile.proposing !== slot) return;
  if (round.accepts.length < majority(config.servers.length)) return;
  const entry = volatile.log[slot];
  volatile.log[slot] = { ...entry, status: 'chosen' };
  delete volatile.phase2[slot];
  volatile.proposing = null;
  const completed = executeContiguous(durable, volatile);
  completed.forEach(({ pending }) => {
    durable.served.push({ slot, client: pending.client, seq: pending.seq });
  });
  replyOutbox(config, nodeId, completed).forEach((m) => outbox.push(m));
  timerOps.push({ action: 'cancel', timerKey: TIMER_ACCEPT });
  broadcast(config, nodeId, pingPayload(nodeId, durable, volatile), config.linkDelay, 'heartbeat').forEach((m) =>
    outbox.push(m)
  );
  proposeNext(config, nodeId, durable, volatile, timerOps, outbox);
  notes.push('slot ' + slot + ' chosen');
}

// ---------------- message handlers ----------------

function onPrepare(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const ballot = event.payload.ballot;
  if (compareBallot(ballot, volatile.promised) <= 0) {
    return {
      durable,
      volatile,
      outbox: [
        send(event.from, { msgType: MSG_PROMISE, ballot: { ...volatile.promised }, rejected: true }, config.linkDelay, 'promise (reject)'),
      ],
      note: 'reject prepare below promise',
    };
  }
  volatile.promised = { ...ballot };
  volatile.leader = null;
  volatile.isLeader = false;
  return {
    durable,
    volatile,
    outbox: [
      send(
        event.from,
        { msgType: MSG_PROMISE, ballot: { ...ballot }, rejected: false, accepted: acceptedEntries(volatile) },
        config.linkDelay,
        'promise'
      ),
    ],
    note: 'promise ballot [' + ballot.n + ', ' + ballot.address + ']',
  };
}

function onPromise(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  if (volatile.isLeader || p.rejected || !sameBallot(p.ballot, volatile.ballot)) {
    return { durable, volatile, note: 'stale promise ignored' };
  }
  if (!volatile.phase1Replies.some((r) => r.from === event.from)) {
    volatile.phase1Replies.push({ from: event.from, ballot: { ...p.ballot }, accepted: p.accepted || [] });
  }
  if (volatile.phase1Replies.length < majority(config.servers.length)) {
    return { durable, volatile, note: 'promise recorded' };
  }
  // Won the election: adopt the highest-ballot value per slot.
  const best = {};
  volatile.phase1Replies.forEach((r) => {
    (r.accepted || []).forEach((e) => {
      if (!best[e.slot] || compareBallot(e.ballot, best[e.slot].ballot) > 0) {
        best[e.slot] = e;
      }
    });
  });
  Object.keys(best).forEach((slot) => {
    const e = best[slot];
    if (e.status === 'chosen') {
      volatile.log[e.slot] = {
        status: 'chosen', ballot: { ...e.ballot }, command: e.command, client: e.client, seq: e.seq,
      };
    } else {
      volatile.log[e.slot] = {
        status: 'accepted', ballot: { ...volatile.ballot }, command: e.command, client: e.client, seq: e.seq,
      };
    }
  });
  volatile.phase1Replies = [];
  volatile.isLeader = true;
  volatile.leader = nodeId;
  volatile.lastLeaderContact = state.virtualTime;
  volatile.executedAll = { [nodeId]: durable.executedThrough };
  const outbox = [];
  const timerOps = [];
  const completed = executeContiguous(durable, volatile);
  completed.forEach(({ pending }) => {
    durable.served.push({ slot: -1, client: pending.client, seq: pending.seq });
  });
  replyOutbox(config, nodeId, completed).forEach((m) => outbox.push(m));
  broadcast(config, nodeId, pingPayload(nodeId, durable, volatile), config.linkDelay, 'heartbeat').forEach((m) =>
    outbox.push(m)
  );
  proposeNext(config, nodeId, durable, volatile, timerOps, outbox);
  return { durable, volatile, outbox, timerOps, note: nodeId + ' leads ballot [' + volatile.ballot.n + ']' };
}

function onAccept(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  if (compareBallot(p.ballot, volatile.promised) < 0) {
    return { durable, volatile, note: 'stale ballot accept ignored' };
  }
  if (compareBallot(p.ballot, volatile.promised) > 0) {
    volatile.promised = { ...p.ballot };
  }
  volatile.leader = event.from;
  volatile.isLeader = false;
  volatile.lastLeaderContact = state.virtualTime;
  const existing = slotOf(volatile.log, p.slot);
  if (
    existing.status === 'empty' ||
    (existing.status === 'accepted' && compareBallot(p.ballot, existing.ballot) >= 0)
  ) {
    volatile.log[p.slot] = {
      status: 'accepted', ballot: { ...p.ballot }, command: p.command, client: p.client, seq: p.seq,
    };
  }
  return {
    durable,
    volatile,
    outbox: [
      send(event.from, { msgType: MSG_ACCEPTED, slot: p.slot, ballot: { ...p.ballot } }, config.linkDelay, 'accepted slot ' + p.slot),
    ],
    note: 'accept slot ' + p.slot,
  };
}

function onAccepted(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  if (!volatile.isLeader) return { durable, volatile, note: 'accepted while not leader ignored' };
  const round = volatile.phase2[p.slot];
  if (!round || !sameBallot(p.ballot, round.ballot)) {
    return { durable, volatile, note: 'stale accepted ignored' };
  }
  if (!round.accepts.includes(event.from)) round.accepts.push(event.from);
  const outbox = [];
  const timerOps = [];
  const notes = ['accepted recorded'];
  checkChosenMajority(config, nodeId, durable, volatile, p.slot, outbox, timerOps, notes);
  return { durable, volatile, outbox, timerOps, note: notes.join('; ') };
}

function onPing(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  if (compareBallot(p.ballot, volatile.promised) < 0) {
    return { durable, volatile, note: 'stale heartbeat ignored' };
  }
  if (compareBallot(p.ballot, volatile.promised) > 0) {
    volatile.promised = { ...p.ballot };
  }
  volatile.leader = p.leader;
  if (p.leader !== nodeId) volatile.isLeader = false;
  volatile.lastLeaderContact = state.virtualTime;
  (p.chosen || []).forEach((e) => {
    const existing = slotOf(volatile.log, e.slot);
    if (existing.status === 'chosen') return;
    if (existing.status === 'empty' || compareBallot(e.ballot, existing.ballot || { n: -1, address: '' }) >= 0) {
      volatile.log[e.slot] = {
        status: 'chosen', ballot: { ...e.ballot }, command: e.command, client: e.client, seq: e.seq,
      };
    }
  });
  const outbox = [];
  const completed = executeContiguous(durable, volatile);
  replyOutbox(config, nodeId, completed).forEach((m) => outbox.push(m));
  if ((p.gcWatermark || -1) > volatile.gcWatermark) {
    volatile.gcWatermark = p.gcWatermark;
  }
  for (let s = 0; s <= volatile.gcWatermark && s <= durable.executedThrough; s++) {
    if (volatile.log[s] && volatile.log[s].status !== 'cleared') {
      volatile.log[s] = { status: 'cleared', ballot: { ...volatile.log[s].ballot } };
    }
  }
  outbox.push(
    send(event.from, { msgType: MSG_PING_REPLY, executedThrough: durable.executedThrough }, config.linkDelay, 'ping reply')
  );
  return { durable, volatile, outbox, note: 'heartbeat learned' };
}

function onPingReply(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  if (!volatile.isLeader) return { durable, volatile, note: 'ping reply while not leader ignored' };
  volatile.executedAll[event.from] = event.payload.executedThrough;
  volatile.executedAll[nodeId] = durable.executedThrough;
  const all = config.servers.map((s) => (volatile.executedAll[s] === undefined ? -1 : volatile.executedAll[s]));
  const watermark = Math.min(...all);
  if (watermark > volatile.gcWatermark) {
    volatile.gcWatermark = watermark;
    for (let s = 0; s <= watermark && s <= durable.executedThrough; s++) {
      if (volatile.log[s] && volatile.log[s].status !== 'cleared') {
        volatile.log[s] = { status: 'cleared', ballot: { ...volatile.log[s].ballot } };
      }
    }
    return { durable, volatile, note: 'compact through slot ' + watermark };
  }
  return { durable, volatile, note: 'ping reply recorded' };
}

function onClientRequest(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  const amoEntry = durable.amo[p.client];
  if (amoEntry && amoEntry.seq === p.seq) {
    return {
      durable,
      volatile,
      outbox: [
        send(p.client, { msgType: MSG_CREPLY, client: p.client, seq: p.seq, result: amoEntry.result, leaderHint: volatile.leader || nodeId }, config.linkDelay, 'cached reply'),
      ],
      note: 'duplicate client request answered from cache',
    };
  }
  if (!volatile.isLeader) {
    if (volatile.leader && volatile.leader !== nodeId) {
      return {
        durable,
        volatile,
        outbox: [
          send(volatile.leader, { msgType: MSG_CREQUEST, client: p.client, seq: p.seq, command: p.command }, config.linkDelay, 'forward to leader'),
        ],
        note: 'forward client request to ' + volatile.leader,
      };
    }
    return { durable, volatile, note: 'no leader known; request dropped' };
  }
  if (!volatile.pendingCommands.some((c) => c.client === p.client && c.seq === p.seq)) {
    volatile.pendingCommands.push({ client: p.client, seq: p.seq, command: p.command });
  }
  const outbox = [];
  const timerOps = [];
  proposeNext(config, nodeId, durable, volatile, timerOps, outbox);
  return { durable, volatile, outbox, timerOps, note: 'leader queues client request' };
}

// ---------------- timers ----------------

function onPingTimer(state, config, nodeId) {
  const node = state.nodes[nodeId];
  const outbox = node.durable.role === 'paxos' && node.volatile.isLeader
    ? broadcast(config, nodeId, pingPayload(nodeId, node.durable, node.volatile), config.linkDelay, 'heartbeat')
    : [];
  return {
    outbox,
    timerOps: [
      {
        action: 'set', timerKey: TIMER_PING, timerType: TIMER_PING, payload: {}, delay: config.pingInterval, label: 'ping timer',
      },
    ],
    note: node.volatile.isLeader ? 'leader heartbeats' : 'ping timer',
  };
}

function onCheckTimer(state, config, nodeId) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const timerOps = [
    {
      action: 'set', timerKey: TIMER_CHECK, timerType: TIMER_CHECK, payload: {}, delay: config.checkInterval, label: 'check timer',
    },
  ];
  if (volatile.isLeader) {
    return { durable, volatile, timerOps, note: 'leader skips election' };
  }
  if (state.virtualTime - volatile.lastLeaderContact < config.electionTimeout) {
    return { durable, volatile, timerOps, note: 'leader fresh; no election' };
  }
  const n = Math.max(volatile.ballot.n, volatile.promised.n) + 1;
  volatile.ballot = { n, address: nodeId };
  volatile.promised = { ...volatile.ballot };
  volatile.leader = null;
  volatile.phase1Replies = [{ from: nodeId, ballot: { ...volatile.ballot }, accepted: acceptedEntries(volatile) }];
  return {
    durable,
    volatile,
    timerOps,
    outbox: broadcast(
      config, nodeId,
      { msgType: MSG_PREPARE, ballot: { ...volatile.ballot } },
      config.linkDelay, 'prepare'
    ),
    note: nodeId + ' campaigns for ballot [' + n + ']',
  };
}

function onAcceptTimer(state, config, nodeId) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  if (!volatile.isLeader || volatile.proposing === null) {
    return { durable, volatile, note: 'stale accept timer discarded' };
  }
  const slot = volatile.proposing;
  const entry = volatile.log[slot];
  return {
    durable,
    volatile,
    outbox: broadcast(
      config, nodeId,
      { msgType: MSG_ACCEPT, slot, ballot: { ...volatile.ballot }, command: entry.command, client: entry.client, seq: entry.seq },
      config.linkDelay, 'accept slot ' + slot + ' (retry)'
    ),
    timerOps: [
      {
        action: 'set', timerKey: TIMER_ACCEPT, timerType: TIMER_ACCEPT, payload: { slot }, delay: config.acceptTimeout, label: 'accept timer',
      },
    ],
    note: 'leader retries accept for slot ' + slot,
  };
}

// ---------------- client ----------------

function clientSend(durable, volatile, nodeId, config, seq, command) {
  return send(
    volatile.target,
    { msgType: MSG_CREQUEST, client: nodeId, seq, command },
    config.linkDelay,
    'client request'
  );
}

function onClientCommand(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  if (volatile.outstanding) {
    return { durable, volatile, note: 'client busy; command ignored' };
  }
  if (!volatile.target) volatile.target = config.servers[0];
  const seq = durable.nextSeq;
  durable.nextSeq += 1;
  durable.accepted += 1;
  durable.sentIds.push(nodeId + '#' + seq);
  volatile.outstanding = { seq, command: event.payload.command };
  volatile.retries = 0;
  return {
    durable,
    volatile,
    outbox: [clientSend(durable, volatile, nodeId, config, seq, event.payload.command)],
    timerOps: [
      {
        action: 'set', timerKey: TIMER_RETRY + ':' + seq, timerType: TIMER_RETRY, payload: { seq }, delay: config.clientRetry, label: 'client retry',
      },
    ],
    note: 'client sends seq ' + seq,
  };
}

function onClientReply(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  if (volatile.outstanding && volatile.outstanding.seq === p.seq) {
    durable.completed.push({ seq: p.seq, result: p.result });
    volatile.outstanding = null;
    volatile.retries = 0;
    if (p.leaderHint) volatile.target = p.leaderHint;
    return {
      durable,
      volatile,
      timerOps: [{ action: 'cancel', timerKey: TIMER_RETRY + ':' + p.seq }],
      note: 'client completes seq ' + p.seq,
    };
  }
  return { durable, volatile, note: 'stale client reply ignored' };
}

function onClientRetry(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const { seq } = event.payload;
  if (!volatile.outstanding || volatile.outstanding.seq !== seq) {
    return { durable, volatile, note: 'stale client timer discarded' };
  }
  volatile.retries = (volatile.retries || 0) + 1;
  if (volatile.retries % 3 === 0) {
    const idx = config.servers.indexOf(volatile.target);
    volatile.target = config.servers[(idx + 1) % config.servers.length];
  }
  return {
    durable,
    volatile,
    outbox: [clientSend(durable, volatile, nodeId, config, seq, volatile.outstanding.command)],
    timerOps: [
      {
        action: 'set', timerKey: TIMER_RETRY + ':' + seq, timerType: TIMER_RETRY, payload: { seq }, delay: config.clientRetry, label: 'client retry',
      },
    ],
    note: 'client retries seq ' + seq + ' via ' + volatile.target,
  };
}

// ---------------- adapter ----------------

export function handleMessage(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node) return { note: 'message to unknown node; ignored' };
  if (node.durable.role === 'paxosclient') {
    if (event.payload.msgType === MSG_CREPLY) return onClientReply(state, config, nodeId, event);
    return { note: 'unexpected client message ignored' };
  }
  switch (event.payload.msgType) {
    case MSG_PREPARE:
      return onPrepare(state, config, nodeId, event);
    case MSG_PROMISE:
      return onPromise(state, config, nodeId, event);
    case MSG_ACCEPT:
      return onAccept(state, config, nodeId, event);
    case MSG_ACCEPTED:
      return onAccepted(state, config, nodeId, event);
    case MSG_PING:
      return onPing(state, config, nodeId, event);
    case MSG_PING_REPLY:
      return onPingReply(state, config, nodeId, event);
    case MSG_CREQUEST:
      return onClientRequest(state, config, nodeId, event);
    default:
      return { note: 'unexpected message ignored' };
  }
}

export function handleClientCommand(state, nodeId, event) {
  const node = state.nodes[nodeId];
  if (!node || node.durable.role !== 'paxosclient') {
    return { note: 'invoke sent to non-client; ignored' };
  }
  return onClientCommand(state, state.config, nodeId, event);
}

export function handleTimer(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node) return { note: 'timer for unknown node; ignored' };
  if (node.durable.role === 'paxosclient') {
    if (event.payload.timerType === TIMER_RETRY) return onClientRetry(state, config, nodeId, event);
    return { note: 'unknown timer; ignored' };
  }
  switch (event.payload.timerType) {
    case TIMER_PING:
      return onPingTimer(state, config, nodeId);
    case TIMER_CHECK:
      return onCheckTimer(state, config, nodeId, event);
    case TIMER_ACCEPT:
      return onAcceptTimer(state, config, nodeId, event);
    default:
      return { note: 'unknown timer; ignored' };
  }
}

export function checkInvariants(state) {
  return checkLab3Invariants(state);
}

export function describeEvent(event) {
  const p = event.payload || {};
  if (event.kind === EventKind.MESSAGE) {
    switch (p.msgType) {
      case MSG_PREPARE:
        return 'deliver prepare [' + p.ballot.n + ']';
      case MSG_PROMISE:
        return 'deliver promise' + (p.rejected ? ' (reject)' : '');
      case MSG_ACCEPT:
        return 'deliver accept slot ' + p.slot;
      case MSG_ACCEPTED:
        return 'deliver accepted slot ' + p.slot;
      case MSG_PING:
        return 'deliver heartbeat';
      case MSG_PING_REPLY:
        return 'deliver heartbeat reply';
      case MSG_CREQUEST:
        return 'deliver client request ' + p.client + '#' + p.seq;
      case MSG_CREPLY:
        return 'deliver client reply ' + p.client + '#' + p.seq;
      default:
        return 'deliver message';
    }
  }
  if (event.kind === EventKind.TIMER) return 'fire timer ' + (p.timerKey || '');
  if (event.kind === EventKind.CLIENT_COMMAND) return 'invoke command';
  return event.label || event.kind;
}

export function summarizeNode(nodeId, node) {
  const { durable, volatile } = node;
  if (durable.role === 'paxosclient') {
    return [
      ['role', 'client'],
      ['target', volatile.target || '(none)'],
      ['completed', String(durable.completed.length)],
      ['outstanding', volatile.outstanding ? '#' + volatile.outstanding.seq : 'none'],
    ];
  }
  const ballot = (b) => '[' + b.n + ', ' + (b.address || '-') + ']';
  const log = volatile.log
    .map((s, i) => (s && s.status !== 'empty' ? i + ':' + s.status : null))
    .filter(Boolean)
    .join(' ');
  return [
    ['role', volatile.isLeader ? 'leader' : 'follower'],
    ['ballot', ballot(volatile.ballot)],
    ['promised', ballot(volatile.promised)],
    ['leader', volatile.leader || '(none)'],
    ['log', log || '(empty)'],
    ['executed', durable.executedThrough >= 0 ? 'through ' + durable.executedThrough : 'none'],
    ['keys', Object.keys(durable.kv).join(', ') || '(empty)'],
    ['gc', volatile.gcWatermark >= 0 ? 'through ' + volatile.gcWatermark : 'none'],
  ];
}


export function sampleCommands(config) {
  const client = config.clients[0];
  return [
    { label: 'PUT x = 1', to: client, command: { op: 'put', key: 'x', value: 1 } },
    { label: 'PUT y = 2', to: client, command: { op: 'put', key: 'y', value: 2 } },
    { label: 'GET x', to: client, command: { op: 'get', key: 'x' } },
    { label: 'APPEND x + "!"', to: client, command: { op: 'append', key: 'x', value: '!' } },
  ];
}

const lab3Adapter = {
  initialize,
  handleMessage,
  handleClientCommand,
  handleTimer,
  freshVolatile,
  checkInvariants,
  describeEvent,
  summarizeNode,
};

export default lab3Adapter;
